/**
 * Minimal inline icon set (currentColor, stroke-based) used across the flow.
 * Kept local + self-contained — no external icon dependency on the customer's
 * critical path (DATAROOM_AUDIT S5 flags CDN assets as slow/blocked on
 * India-mobile / NRI networks).
 */

export type IconName =
  | 'shield'
  | 'shield-check'
  | 'lock'
  | 'clock'
  | 'check'
  | 'users'
  | 'monitor'
  | 'autoconnect'
  | 'doc'
  | 'face'
  | 'mappin'
  | 'camera'
  | 'mic'
  | 'cam'
  | 'phone-end'
  | 'chevron'
  | 'upload'
  | 'info';

interface IconProps {
  name: IconName;
  size?: number;
  className?: string;
}

const PATHS: Record<IconName, React.ReactNode> = {
  shield: (
    <path
      d="M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinejoin="round"
    />
  ),
  'shield-check': (
    <>
      <path
        d="M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
      <path
        d="M9 12l2 2 4-4"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </>
  ),
  lock: (
    <>
      <rect x="4" y="10" width="16" height="10" rx="2.5" stroke="currentColor" strokeWidth="1.8" />
      <path d="M8 10V7a4 4 0 018 0v3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="8.2" stroke="currentColor" strokeWidth="1.7" />
      <path d="M12 8v4.4l2.8 1.7" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
  check: (
    <path d="M5 12l4.5 4.5L19 7" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  ),
  users: (
    <>
      <circle cx="9" cy="9" r="3.4" stroke="currentColor" strokeWidth="1.7" />
      <path d="M3.5 19c0-3 2.5-5 5.5-5s5.5 2 5.5 5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      <circle cx="17" cy="8" r="2.6" stroke="currentColor" strokeWidth="1.7" />
      <path d="M16 14c2.6.2 4.5 2.1 4.5 5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </>
  ),
  monitor: (
    <>
      <rect x="4" y="4" width="16" height="16" rx="4" stroke="currentColor" strokeWidth="1.7" />
      <circle cx="12" cy="12" r="2.4" fill="currentColor" />
    </>
  ),
  autoconnect: (
    <>
      <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      <path d="M12 4l3 3-3 3" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
  doc: (
    <>
      <rect x="4" y="5" width="16" height="14" rx="2.5" stroke="currentColor" strokeWidth="1.7" />
      <path d="M8 10h5M8 14h8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </>
  ),
  face: (
    <>
      <circle cx="12" cy="9" r="4" stroke="currentColor" strokeWidth="1.7" />
      <path d="M5 20c0-3.3 3.1-5.5 7-5.5s7 2.2 7 5.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </>
  ),
  mappin: (
    <>
      <path d="M12 21c4-3.5 7-6.6 7-10a7 7 0 10-14 0c0 3.4 3 6.5 7 10z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
      <circle cx="12" cy="11" r="2.3" stroke="currentColor" strokeWidth="1.7" />
    </>
  ),
  camera: (
    <>
      <path d="M4 8h3l1.5-2h7L17 8h3v11H4z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
      <circle cx="12" cy="13" r="3.4" stroke="currentColor" strokeWidth="1.7" />
    </>
  ),
  mic: (
    <>
      <rect x="9" y="3" width="6" height="12" rx="3" stroke="currentColor" strokeWidth="1.8" />
      <path d="M6 11a6 6 0 0012 0M12 17v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </>
  ),
  cam: (
    <>
      <rect x="3" y="7" width="12" height="10" rx="2.5" stroke="currentColor" strokeWidth="1.8" />
      <path d="M15 11l6-3v8l-6-3" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    </>
  ),
  'phone-end': (
    <path
      d="M4 9c5-3 11-3 16 0 1 .6 1.5 1.7 1 2.8l-1 2c-.4.9-1.5 1.3-2.4.9l-2.3-1c-.8-.3-1.3-1.1-1.2-2l.1-1.2c-2-.7-4.2-.7-6.2 0l.1 1.2c.1.9-.4 1.7-1.2 2l-2.3 1c-.9.4-2 0-2.4-.9l-1-2C2.5 10.7 3 9.6 4 9z"
      fill="currentColor"
    />
  ),
  chevron: (
    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
  ),
  upload: (
    <>
      <path d="M12 15V4M8 8l4-4 4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4 15v3a2.5 2.5 0 002.5 2.5h11A2.5 2.5 0 0020 18v-3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="8.2" stroke="currentColor" strokeWidth="1.7" />
      <path d="M12 11v5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <circle cx="12" cy="8" r="1.1" fill="currentColor" />
    </>
  ),
};

export function Icon({ name, size = 18, className }: IconProps): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      className={className}
      aria-hidden="true"
    >
      {PATHS[name]}
    </svg>
  );
}
